import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { throughVendorService, getErrorMessage } from '../../../../services/apiService';
import { useShellToast } from '../useShellToast';
import { suggestAsks } from '../data/regularizationInsights';
import { asksForVendors } from '../data/throughVendor';
import { SuggestionRow } from './RegularizationInsights';

/**
 * "Who needs asking" — worked out from the records and the links already sent, with the one click
 * that acts on each suggestion. Both regularization hubs render this same component, so the
 * suggestions and what a click does can never differ between them.
 */

const num = (n) => Number(n || 0).toLocaleString('en-IN');

/** The send wizard, with these vendors and these asks already chosen. */
export function sendLinkPath(vendorIds, askKeys) {
  return `/campaigning-tool/regularization/send?vendors=${vendorIds.join(',')}${askKeys?.length ? `&ask=${askKeys.join(',')}` : ''}`;
}

/** A reminder on the same link, reported back as a toast. `onDone` refreshes whatever the screen shows. */
export function useRemind(onDone) {
  const toast = useShellToast();
  const [busyKey, setBusyKey] = useState(null);
  const remind = async (key, vendorIds) => {
    setBusyKey(key);
    try {
      const res = await throughVendorService.remind({ vendor_ids: vendorIds });
      const d = res.data || {};
      const parts = [`Reminder sent to ${num(d.reminded)} vendor${d.reminded === 1 ? '' : 's'}`];
      if (d.skipped?.length) parts.push(`${num(d.skipped.length)} skipped (${[...new Set(d.skipped.map((s) => s.reason))].join('; ')})`);
      if (d.failed?.length) parts.push(`${num(d.failed.length)} could not be sent`);
      toast(parts.join(' · '), { tone: d.failed?.length ? 'warning' : d.reminded ? 'success' : 'info' });
      onDone?.();
    } catch (err) {
      toast(getErrorMessage(err) || 'Could not send reminders', { tone: 'error' });
    } finally {
      setBusyKey(null);
    }
  };
  return { busyKey, remind };
}

/**
 * base — every vendor; stateOf — useVendorState().stateOf; links — useLatestLinks(); ready — the
 * three have loaded; disabled — the viewer cannot send; onReminded — refresh after a reminder.
 */
export function WhoNeedsAsking({ base, stateOf, links, ready, disabled, onReminded }) {
  const navigate = useNavigate();
  const { busyKey, remind } = useRemind(onReminded);
  const byId = links.byId;
  const linkOf = useMemo(() => (id) => byId.get(id) || null, [byId]);
  const suggestions = useMemo(() => (ready ? suggestAsks(base, stateOf, linkOf) : []), [ready, base, stateOf, linkOf]);

  /** A fresh link asks what the expired one asked; every other suggestion carries its own asks. */
  const asksOf = (s) => (s.kind === 'resend' ? asksForVendors(s.vendorIds, linkOf) : s.asks);
  const act = (s) => (s.kind === 'remind' ? remind(s.key, s.vendorIds) : navigate(sendLinkPath(s.vendorIds, asksOf(s))));

  return (
    <SuggestionRow
      title="Who needs asking"
      subtitle="Worked out from the records and the links already sent — one click opens the send with them chosen."
      suggestions={suggestions}
      actionLabelFor={(s) => (s.kind === 'remind' ? `Remind ${num(s.vendorIds.length)}` : s.kind === 'resend' ? `Resend to ${num(s.vendorIds.length)}` : `Ask ${num(s.vendorIds.length)}`)}
      onAct={act}
      onPreview={(s) => navigate(sendLinkPath(s.vendorIds, asksOf(s)))}
      disabled={disabled || !ready}
      busyKey={busyKey}
    />
  );
}

export default WhoNeedsAsking;
